import { useMemo } from 'react';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { canAccessPrincipalFeatures } from '../utils/roles';
import { getFeatureAccessLookupKey } from '../utils/staffPermissionBlocks';
import { getStaffFeatureAccessRow, isEmptyAccessRow } from '../utils/staffFeatureAccessPolicy';
import type { SchoolsResponse } from '../types';

/**
 * Returns view/edit permission for a feature for the current user.
 * Principal/Admin always get canView + canEdit. Others use school settings maps
 * (role or authority mode) via {@link getStaffFeatureAccessRow}.
 * No saved row or empty row denies access (ROLE and AUTHORITY).
 */
export function useFeatureAccess(featureId: string): { canView: boolean; canEdit: boolean; isPrincipal: boolean; loading: boolean } {
  const user = authService.getStoredUser();
  const isPrincipal = !!user && canAccessPrincipalFeatures(user.role);
  const { data: schoolsData, isLoading } = useQuery<SchoolsResponse>(
    'schools',
    async () => {
      const r = await apiClient.get('/schools');
      return r.data as SchoolsResponse;
    },
    { enabled: !!user }
  );
  const school = schoolsData?.schools?.[0];
  const lookupKey = getFeatureAccessLookupKey(user, school);

  return useMemo(() => {
    if (isPrincipal) return { canView: true, canEdit: true, isPrincipal: true, loading: isLoading };
    if (isLoading) return { canView: false, canEdit: false, isPrincipal: false, loading: true };
    if (!lookupKey) return { canView: false, canEdit: false, isPrincipal: false, loading: false };
    const roleAccess = getStaffFeatureAccessRow(school, lookupKey);
    if (isEmptyAccessRow(roleAccess)) {
      return { canView: false, canEdit: false, isPrincipal: false, loading: false };
    }
    const perms = roleAccess![featureId];
    return {
      canView: !!perms?.view,
      canEdit: !!perms?.edit,
      isPrincipal: false,
      loading: false,
    };
  }, [isPrincipal, isLoading, lookupKey, school, featureId]);
}
